import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

import { UserInfo, UserService } from '../../services/user/user.service';
import { AwardProgramInfo, AwardsService } from '../../services/awards/awards.service';
import { ApplicationService, NomineeTypeInfo, Select, JudgeNominationScoresInfo, JudgeScoresInfo, NominationWindowInfo } from '../../services/application/application.service';
import { ErrorService } from '../../services/error/error.service';
import { ScrollService } from '../../services/scroll/scroll.service';
import { SplashService } from '../../services/splash/splash.service';
import { DialogService } from '../../services/dialogs/dialogs.service';
import { BaseConfirmationDialogOptions } from '../../services/dialogs/confirmation/confirmation.dialogcomponent';
import { BaseTieBreakerDialogOptions } from '../../services/dialogs/tiebreaker/tiebreaker.dialogcomponent';
import { BaseTieOverrideDialogOptions } from '../../services/dialogs/tieoverride/tieoverride.dialogcomponent';
import { SettingsService } from '../../services/settings/settings.service';

@Component({
    selector: 'app-finalizenominees',
    templateUrl: './finalizenominees.component.html',
    styleUrls: ['./finalizenominees.component.scss']
})

export class FinalizeNomineesComponent implements OnInit, OnDestroy {
    private _subrelease: Subject<void> = new Subject<void>();
    User: UserInfo;
    Award: AwardProgramInfo;
    ViewReady: boolean;
    NoResults: boolean;
    Employee: boolean;
    NominationIndex: number;
    DistrictIndex: number;
	SelectedPhase: number;
	SchoolYear: string;
    NomTypes: NomineeTypeInfo[];
    Windows: NominationWindowInfo[];
    PhaseScores: JudgeNominationScoresInfo[];
    Phases: Select[] = [
        { Value: 1, Display: 'Initial Judging' },
        { Value: 2, Display: 'Initial Tie-Breaker' },
        { Value: 3, Display: 'Final Judging' },
        { Value: 4, Display: 'Final Tie-Breaker' }
    ];

    constructor(private appSvc: ApplicationService,
        private awardSvc: AwardsService,
        private dlgService: DialogService,
        private errSvc: ErrorService,
        private scrollSvc: ScrollService,
        private splashService: SplashService,
		private usrSvc: UserService,
		private settingsSvc: SettingsService) { }

    ngOnInit() {
        this.User = this.usrSvc.User;
        this.Award = this.awardSvc.CurrentAwards;
        this.SelectedPhase = 0;
        this.NominationIndex = 0;
        this.DistrictIndex = 0;
        this.ViewReady = false;

		this.settingsSvc.CurrentSchoolYearSubject.pipe(takeUntil(this._subrelease)).subscribe(year => {
			this.SchoolYear = year;

			let nomTypes$ = this.appSvc.GetNomineeTypes();
			let windows$ = this.appSvc.GetNominationWindows(year);

			combineLatest(nomTypes$, windows$).pipe(
				takeUntil(this._subrelease),
				map(_pck => {
					return {
						nomTypes: _pck[0],
						windows: _pck[1]
					};
				})
			).subscribe(packet => {
				this.NomTypes = packet.nomTypes;
				this.Windows = packet.windows;

				let currentNomType = packet.nomTypes.find(f => f.AwardID === this.Award.AwardID);
				this.Employee = currentNomType.PersonType === 'EMPLOYEE';
			}, error => this.errSvc.HandleServiceError(error));
		});
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ChangeDistrict(index: number) {
        this.DistrictIndex = index;
        this.scrollSvc.TriggerScroll();
    }

    ChangeNomination(index: number) {
        this.NominationIndex = index;
        this.DistrictIndex = 0;
        this.scrollSvc.TriggerScroll();
    }

    FinalizeWinner(nominee: JudgeScoresInfo) {
        let nomination = this.PhaseScores[this.NominationIndex];
        let dist = nomination.DistrictList[this.DistrictIndex];

        let dOptions = new BaseConfirmationDialogOptions();
        dOptions.MessageText = `You are about to mark the following nominee as the winner for this judging round:<br /><br />` +
            `<strong>Category:</strong> ${dist.BoardDistrict}<br />` +
            `<strong>Nomination #:</strong> ${nominee.NominationID}<br />` +
            `<strong>Name:</strong> ${nominee.FirstName} ${nominee.LastName}<br />` +
            `<strong>Site:</strong> ${nominee.SiteName} (${nominee.SiteLocation})<br /><br />` +
            `Once finalized, this nominee will be marked as the winner for ` +
            `${(nomination.JudgingPhase < 3) ? ('this district') : ('this nomination')}. Are you sure you wish to proceed?`;

        this.dlgService.OpenConfirmationDialog(dOptions).pipe(takeUntil(this._subrelease)).subscribe(res => {
            if (res === true) {
                this.splashService.OpenSplash();

                this.appSvc.FinalizeWinner(
                    this.User.AuthToken,
                    this.User.CurrentRole,
					this.Award.AwardID,
					this.SchoolYear,
                    nomination.JudgingPhase,
                    nominee.NominationID,
                    nomination.NomineeTypeID,
                    dist.BoardDistrict,
                    nominee.FinalScore,
                    false
                ).pipe(takeUntil(this._subrelease)).subscribe(refresh => {
                    this.PhaseScores = refresh;
                    this.splashService.CloseSplash();
                }, error => {
                    this.splashService.CloseSplash();
                    this.errSvc.HandleServiceError(error);
                });
            }
        });
    }

    GetNomName(nomTypeID: number) {
        return this.NomTypes.find(f => f.NomineeTypeID === nomTypeID).Name;
    }

    NominationComplete(nom: JudgeNominationScoresInfo): boolean {
        if (nom.DistrictList.every(d => d.WinnerFinalized === true)) {
            return true;
        }
        return false;
    }

    OverrideTie() {
        let tiedNoms = this.PhaseScores[this.NominationIndex].DistrictList[this.DistrictIndex].Scores;
        let opts: BaseTieOverrideDialogOptions = {
            tiedNominees: tiedNoms
        };

        this.dlgService.OpenTieOverrideDialog(opts).pipe(takeUntil(this._subrelease)).subscribe(res => {
            if (res != null) {
                let nom = res[0];
                this.appSvc.HandleSpecialTieBreaker(
                    this.User.AuthToken,
                    this.User.CurrentRole,
					this.Award.AwardID,
					this.SchoolYear,
                    nom.NomineeTypeID,
                    nom.JudgingPhase,
                    res
                ).pipe(takeUntil(this._subrelease)).subscribe(refresh => {
                    this.PhaseScores = refresh.Winners;
                }, error => this.errSvc.HandleServiceError(error));
            }
        });
    }

    RetrieveScores() {
        this.splashService.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Retrieving Scores...' });

		this.appSvc.GetCurrentJudgeScoresByPhase(this.User.AuthToken, this.User.CurrentRole, this.Award.AwardID, this.SelectedPhase, this.SchoolYear).pipe(takeUntil(this._subrelease)).subscribe(res => {
            this.NoResults = res.length === 0;
            this.PhaseScores = res;
            this.NominationIndex = 0;
            this.DistrictIndex = 0;
            this.splashService.CloseSplash();
            this.ViewReady = true;
        }, error => {
            this.splashService.CloseSplash();
            this.errSvc.HandleServiceError(error);
        });
    }

    StartTieBreaker() {
        let tiePhaseCode: string;
        let noms = this.PhaseScores[this.NominationIndex];
        let currentPhaseID = noms.JudgingPhase;
        let nominees = noms.DistrictList[this.DistrictIndex].Scores;

        if (currentPhaseID === 1) {
            tiePhaseCode = 'INITJUDGETIE';
        }
        else if (currentPhaseID === 3) {
            tiePhaseCode = 'FINALJUDGETIE';
        }
        else {
            return; //can't make tie-breaker from tie-breaker
        }

        let tiedNoms = nominees.filter(n => n.WinnerTie === true);
        let winNeeded = this.Windows.find(w => w.NomineeTypeID === noms.NomineeTypeID && w.WindowTypeCode === tiePhaseCode) == null;

        let opts: BaseTieBreakerDialogOptions = {
            tiedNominees: tiedNoms,
            windowNeeded: winNeeded
        };

        this.dlgService.OpenTieBreakerDialog(opts).pipe(takeUntil(this._subrelease)).subscribe(res => {
            if (res != null) {
                this.appSvc.ScheduleTieBreaker(
                    this.User.AuthToken,
                    this.User.CurrentRole,
					this.Award.AwardID,
					this.SchoolYear,
                    noms.NomineeTypeID,
                    currentPhaseID,
                    tiedNoms,
                    res.AutoAssign,
                    winNeeded,
                    res.BeginDate,
                    res.EndDate
                ).pipe(takeUntil(this._subrelease)).subscribe(refresh => {
                    this.PhaseScores = refresh.Winners;
                }, error => this.errSvc.HandleServiceError(error));
            }
        });
    }

    ViewDetails(index: number) {
        this.PhaseScores[this.NominationIndex].DistrictList[this.DistrictIndex].Scores[index].DetailsShown = !this.PhaseScores[this.NominationIndex].DistrictList[this.DistrictIndex].Scores[index].DetailsShown;
    }
    
}
